/**
 * 報表 Reports (S11.1) — the five pink reports on a route-derived tab shell.
 *
 * Tabs are ROUTE-DERIVED (`/auth/reports/:report`, route-derived selection
 * tenet, mirroring the OCC lens workbench); an unknown key falls back to the
 * landing report. Reports are read-only lenses over the SO / PO / finance /
 * OCC stores; the Back-Order report shares the OCC 待出量 selector so its
 * figure equals the OCC board's by construction.
 */

import { Tabs } from 'antd';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageContainer from '@/components/common/PageContainer';
import PageHeader from '@/components/common/PageHeader';
import { REPORT_DEFAULT, REPORT_DEFS, isReportKey } from './reportsRegistry';
import type { ReportKey } from './reportsRegistry';
import OrderMarginReport from './reports/OrderMarginReport';
import BackOrderReport from './reports/BackOrderReport';
import UnreceivedReport from './reports/UnreceivedReport';
import ArOutstandingReport from './reports/ArOutstandingReport';
import ApOutstandingReport from './reports/ApOutstandingReport';

const REPORT_CONTENT: Record<ReportKey, () => JSX.Element> = {
  'order-margin': OrderMarginReport,
  'back-order': BackOrderReport,
  unreceived: UnreceivedReport,
  receivables: ArOutstandingReport,
  payables: ApOutstandingReport,
};

const ReportsPage = () => {
  const { report } = useParams<{ report: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['reports', 'common']);

  // Route-derived selection: an unknown report key falls back to the landing.
  if (!isReportKey(report)) {
    return <Navigate to={`/auth/reports/${REPORT_DEFAULT}`} replace />;
  }

  const ActiveReport = REPORT_CONTENT[report];

  return (
    <PageContainer>
      <PageHeader title={t('reports:reports.title')} />

      <Tabs
        activeKey={report}
        onChange={(key) => navigate(`/auth/reports/${key}`)}
        items={REPORT_DEFS.map((def) => ({
          key: def.key,
          label: t(`reports:${def.tabKey}`),
        }))}
      />

      <ActiveReport />
    </PageContainer>
  );
};

export default ReportsPage;
